'use client'

import { useLayoutEffect, useRef, ReactNode } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { createScrollAnimation } from '@/lib/animation-utils'

gsap.registerPlugin(ScrollTrigger)

type PropsType = {
  children: ReactNode
  className?: string
  animation?:
    | 'fade-scale'
    | 'fade-up'
    | 'fade-down'
    | 'fade-left'
    | 'fade-right'
    | 'slide-up'
    | 'slide-down'
    | 'slide-left'
    | 'slide-right'
    | 'scale'
    | 'scale-rotate'
    | 'bounce-in'
    | 'rotate-in'
    | 'flip-in'
    | 'blur-in'
    | 'blur-out'
  staggerDelay?: number
  immediate?: boolean
  startOffset?: number
  endOffset?: number
  enabled?: boolean
}

const AnimatedList = ({
  children,
  className = '',
  animation = 'fade-scale',
  staggerDelay = 0.05,
  immediate = true,
  startOffset = 85,
  endOffset,
  enabled = true
}: PropsType) => {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!ref.current || !enabled) {
      return
    }

    const el = ref.current
    const items = Array.from(el.children) as HTMLElement[]

    const config: Record<string, gsap.TweenVars> = {
      'fade-scale': { scale: 0.8, opacity: 0 },
      'fade-up': { y: 60, opacity: 0 },
      'fade-down': { y: -60, opacity: 0 },
      'fade-left': { x: 80, opacity: 0 },
      'fade-right': { x: -80, opacity: 0 },
      'slide-up': { y: 100, opacity: 0 },
      'slide-down': { y: -100, opacity: 0 },
      'slide-left': { x: 120, opacity: 0 },
      'slide-right': { x: -120, opacity: 0 },
      scale: { scale: 0.9, opacity: 0 },
      'scale-rotate': { scale: 0.8, rotation: -10, opacity: 0 },
      'bounce-in': { scale: 0.5, opacity: 0 },
      'rotate-in': { rotation: -180, scale: 0.5, opacity: 0 },
      'flip-in': { rotationY: 90, opacity: 0 },
      'blur-in': { filter: 'blur(10px)', opacity: 0 },
      'blur-out': { filter: 'blur(0px)', opacity: 1 }
    }

    const fromVars = config[animation]
    const toVars = { y: 0, x: 0, scale: 1, opacity: 1, rotation: 0, rotationY: 0, filter: 'blur(0px)' }

    const ctx = gsap.context(async () => {
      await createScrollAnimation(items, el, fromVars, toVars, staggerDelay, immediate, startOffset, endOffset)
    }, ref)

    return () => ctx.revert()
  }, [animation, staggerDelay, immediate, startOffset, endOffset, enabled])

  // When disabled, render a plain container with no initial opacity:0 and no GSAP —
  // the content just appears normally with no animation at all.
  if (!enabled) {
    return <div className={className}>{children}</div>
  }

  return (
    <div ref={ref} className={className} style={{ opacity: 0 }}>
      {children}
    </div>
  )
}

export default AnimatedList
